import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormHelperText,
  Box,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import { Gender } from "../../../../utils";

// Helper function to get current local date/time in a "datetime-local" accepted format.
const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = ("0" + (now.getMonth() + 1)).slice(-2);
  const day = ("0" + now.getDate()).slice(-2);
  const hours = ("0" + now.getHours()).slice(-2);
  const minutes = ("0" + now.getMinutes()).slice(-2);
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const EditPatientDialog = ({ open, onClose, patient, onSave, token }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    age: "",
    blood_type: "",
    phone: "",
    passport: "",
    address: "",
    dosia_file_id: "",
    registration_date: getCurrentDateTimeLocal(),
    followUpBy: "",
  });
  const [errors, setErrors] = useState({});
  const [filterValue, setFilterValue] = useState("");
  const timerRef = useRef(null);
  const [openBloodTypeDialog, setOpenBloodTypeDialog] = useState(false);

  const { data: bloodTypes } = useFetchObjects(
    ["blood-types"],
    "blood-types/",
    token
  );

  const { data: usersData } = useFetchObjects(
    ["users"],
    "users/?position=1",
    token
  );

  // Client search functionality
  const {
    data: clientData,
    isLoading: isLoadingClient,
    isError: isErrorClient,
    refetch: refetchClient,
  } = useFetchObjects(
    ["search-client"],
    `search-client/?contact=${filterValue}`,
    token,
    { enabled: false }
  );

  useEffect(() => {
    if (patient) {
      setFormData({
        firstName: patient.firstname || "",
        lastName: patient.lastname || "",
        gender: patient.gender || "",
        age: patient.age || "",
        blood_type: patient.blood_type || "",
        passport: patient.passport || "",
        address: patient.address || "",
        phone: patient.phone || "",
        dosia_file_id: patient.dosia_file_id || "",
        registration_date:
          patient.registration_date || getCurrentDateTimeLocal(),
        followUpBy: patient.follow_up_by || "",
      });
    }
  }, [patient]);

  // Phone number formatter
  const formatPhoneForSearch = (phone) => {
    if (phone.startsWith("+")) return phone;
    if (phone.startsWith("0")) return phone.substring(1);
    return phone;
  };

  // Client search effect
  useEffect(() => {
    const phoneTrim = formData.phone.trim();
    const isValidPhone = phoneTrim !== "" && /^[+]?\d+$/.test(phoneTrim);
    if (isValidPhone) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const formattedPhone = formatPhoneForSearch(phoneTrim);
        setFilterValue(formattedPhone);
      }, 300);
    } else {
      setFilterValue("");
    }
  }, [formData.phone]);

  useEffect(() => {
    if (filterValue.trim() !== "") {
      refetchClient();
    }
  }, [filterValue, refetchClient]);

  const handleClientSelect = (client) => {
    const fullName = client.full_name || "";
    const nameParts = fullName.split(" ");
    const firstNameFromClient = nameParts[0] || "";
    const lastNameFromClient = nameParts.slice(1).join(" ") || "";
    const followUpId =
      client.follow_up_by &&
      (typeof client.follow_up_by === "object"
        ? client.follow_up_by.id
        : client.follow_up_by);

    setFormData((prev) => ({
      ...prev,
      firstName: firstNameFromClient,
      lastName: lastNameFromClient,
      phone: client.contact || "",
      address: client.address || "",
      age: client.age || "",
      gender: client.gender || "",
      followUpBy: followUpId || "",
    }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!formData.firstName) {
      newErrors.firstName = t("reception.firstNameRequired");
      isValid = false;
    }


    if (formData.dosia_file_id && !/^\d+$/.test(formData.dosia_file_id)) {
      newErrors.dosia_file_id = t("reception.invalidDosiaFileId");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "phone") {
      setFormData((prev) => ({
        ...prev,
        phone: value,
      }));
      setFilterValue("");
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = () => {
    if (validateForm()) {
      const patientData = {
        id: patient.id,
        firstname: formData.firstName,
        lastname: formData.lastName || null,
        age: formData.age ? formData.age : null,
        gender: formData.gender,
        blood_type: formData.blood_type || null,
        phone: formatPhoneForSearch(formData.phone.trim()),
        passport: formData.passport,
        address: formData.address || null,
        dosia_file_id: formData.dosia_file_id || null,
        registration_date: formData.registration_date,
        follow_up_by: formData.followUpBy || null,
      };

      onSave(patientData);
    }
  };

  const handleOpenBloodTypeDialog = () => setOpenBloodTypeDialog(true);
  const handleCloseBloodTypeDialog = () => setOpenBloodTypeDialog(false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
        {t("reception.editPatient")}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {/* Display matching client options */}
        {!isLoadingClient &&
          !isErrorClient &&
          clientData &&
          clientData.length > 0 && (
            <Paper
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: "#e0f7fa",
                borderRadius: "8px",
              }}
              elevation={2}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                {t("call_center.clientView")}
              </Typography>
              {clientData.map((client) => (
                <Box
                  key={client.id}
                  sx={{
                    p: 1,
                    mb: 1,
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                    cursor: "pointer",
                  }}
                  onClick={() => handleClientSelect(client)}
                >
                  <Typography>
                    {t("call_center.fullName")}: {client.full_name}
                  </Typography>
                  <Typography>
                    {t("call_center.address")}:{" "}
                    {client.address ||
                      t("call_center.noAddress", "No Address Provided")}
                  </Typography>
                  <Typography>
                    {t("call_center.contact")}: {client.contact}
                  </Typography>
                  <Typography>
                    {t("call_center.additionalNumber")}: {client.contact_2}
                  </Typography>
                  <Typography>
                    {t("call_center.followUpBy")}:{" "}
                    {client?.display_user
                      ? `${client.display_user.first_name} ${client.display_user.last_name}`
                      : t("call_center.notAssigned")}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("reception.firstName")}
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              error={!!errors.firstName}
              helperText={errors.firstName}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("reception.lastName")}
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("reception.phone")}
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("reception.passport")}
              name="passport"
              value={formData.passport}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("reception.age")}
              name="age"
              type="number"
              value={formData.age}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              margin="normal"
              error={!!errors.gender}
              required
            >
              <InputLabel>{t("reception.gender")}</InputLabel>
              <Select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                label={t("reception.gender")}
              >
                {Gender.map((item) => (
                  <MenuItem value={item.id} key={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.gender && (
                <FormHelperText>{errors.gender}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>{t("reception.blood_type")}</InputLabel>
              <Select
                name="blood_type"
                value={formData.blood_type}
                onChange={handleInputChange}
                label={t("reception.blood_type")}
              >
                {bloodTypes?.results?.map((item) => (
                  <MenuItem value={item.id} key={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("reception.dosiaFileId")}
              name="dosia_file_id"
              value={formData.dosia_file_id}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              error={!!errors.dosia_file_id}
              helperText={errors.dosia_file_id || t("reception.optional")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("reception.address")}
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              multiline
              rows={3}
              error={!!errors.address}
              helperText={errors.address || t("reception.optional")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("reception.registrationDate")}
              type="datetime-local"
              name="registration_date"
              value={formData.registration_date}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={usersData || []}
              getOptionLabel={(option) =>
                `${option.first_name} ${option.last_name}`
              }
              value={
                (usersData &&
                  formData.followUpBy &&
                  usersData.find((u) => u.id === formData.followUpBy)) ||
                null
              }
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  followUpBy: newValue ? newValue.id : "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  label={t("reception.followUpBy")}
                  margin="normal"
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Box display="flex" justifyContent="flex-end" width="100%" gap={2}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="primary"
            size="medium"
            sx={{ minWidth: 100 }}
          >
            {t("reception.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            size="medium"
            sx={{ minWidth: 100, backgroundColor: "#071952" }}
          >
            {t("reception.save")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditPatientDialog;
